import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  ChevronDown,
  FileText,
  Home,
  Mail,
  Menu,
  MessageSquare,
  Settings,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
  description?: string;
}

const navItems: NavItem[] = [
  {
    title: "Dashboard",
    href: "/admin/email",
    icon: <Home className="h-5 w-5" />,
    description: "Email management overview",
  },
  {
    title: "Monitoring",
    href: "/admin/email/monitoring",
    icon: <BarChart3 className="h-5 w-5" />,
    description: "Track email metrics and performance",
  },
  {
    title: "Performance",
    href: "/admin/email/performance",
    icon: <BarChart3 className="h-5 w-5" />,
    description: "Email performance metrics",
  },
  {
    title: "Bounce Management",
    href: "/admin/email/bounce-management",
    icon: <Mail className="h-5 w-5" />,
    description: "Handle bounces and complaints",
  },
  {
    title: "List Management",
    href: "/admin/email/list-management",
    icon: <Users className="h-5 w-5" />,
    description: "Manage subscribers and segments",
  },
  {
    title: "Templates",
    href: "/admin/email/templates",
    icon: <MessageSquare className="h-5 w-5" />,
    description: "Email templates and content",
  },
  {
    title: "Preferences Center",
    href: "/admin/email/preferences",
    icon: <Settings className="h-5 w-5" />,
    description: "Email preference management",
  },
  {
    title: "Privacy Policy",
    href: "/email/privacy-policy",
    icon: <ShieldCheck className="h-5 w-5" />,
    description: "Email privacy and compliance",
  },
  {
    title: "Terms of Service",
    href: "/email/terms",
    icon: <FileText className="h-5 w-5" />,
    description: "Email terms and conditions",
  },
];

export function EmailNavigation() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full border-b bg-background/95 backdrop-blur-sm">
      <div className="container flex h-14 items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[400px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-500" />
                  <span className="font-bold">Email Management</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <Separator className="my-4" />
              <div className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                      location.pathname === item.href
                        ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 font-medium"
                        : "hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-blue-600 dark:hover:text-blue-400"
                    )}
                  >
                    {item.icon}
                    <div>
                      <div>{item.title}</div>
                      {item.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.description}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>

          <Link to="/admin/email" className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            <span className="font-bold hidden md:inline-block">
              Email Management
            </span>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1 overflow-x-auto">
          {navItems.slice(0, 5).map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors",
                location.pathname === item.href
                  ? "bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 font-medium"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-blue-600 dark:hover:text-blue-400"
              )}
            >
              {item.icon}
              <span>{item.title}</span>
            </Link>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 h-8 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <Settings className="h-4 w-4" />
                <span>More</span>
                <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {navItems.slice(5).map((item) => (
                <DropdownMenuItem key={item.href} asChild>
                  <Link
                    to={item.href}
                    className={cn(
                      "flex w-full items-center gap-2",
                      location.pathname === item.href
                        ? "font-medium text-blue-600 dark:text-blue-400"
                        : ""
                    )}
                  >
                    {item.icon}
                    <span>{item.title}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </div>
  );
}
